import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Briefcase, CheckCircle, Sparkles, ArrowRight, Loader2, Check, AlertCircle, Users, Coins, Gift, Zap, Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { referrerLookupService, ReferrerInfo } from '../lib/referrerLookupService';
import { ConnectionReason } from '../lib/referralService';
import { validateAndFormatUSPhone } from '../lib/phoneValidation';

export function WaitlistSignup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    role: 'job_seeker' as 'job_seeker' | 'employer',
    referralSource: '',
    referrerContact: '',
    connectionReason: '' as ConnectionReason | '',
    connectionReasonOther: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [referrerInfo, setReferrerInfo] = useState<ReferrerInfo | null>(null);
  const [lookingUpReferrer, setLookingUpReferrer] = useState(false);
  const [referrerError, setReferrerError] = useState('');

  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam) {
      setFormData((prev) => ({ ...prev, referrerContact: refParam }));
      lookupReferrer(refParam);
    }
  }, [searchParams]);

  const lookupReferrer = async (contact: string) => {
    if (!contact || contact.trim() === '') {
      setReferrerInfo(null);
      setReferrerError('');
      return;
    }

    setLookingUpReferrer(true);
    setReferrerError('');

    try {
      const info = await referrerLookupService.findReferrerByContact(contact);

      if (info) {
        if (!info.account_complete) {
          setReferrerError('This account is not fully set up yet. Please ask them to complete their profile.');
          setReferrerInfo(null);
        } else {
          setReferrerInfo(info);
          setReferrerError('');
        }
      } else {
        setReferrerError('No account found with this email, phone, or code.');
        setReferrerInfo(null);
      }
    } catch (err) {
      setReferrerError('Error looking up referrer.');
      setReferrerInfo(null);
    } finally {
      setLookingUpReferrer(false);
    }
  };

  const handleReferrerContactChange = (value: string) => {
    setFormData({ ...formData, referrerContact: value });

    const timeoutId = setTimeout(() => {
      lookupReferrer(value);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.referrerContact && !referrerInfo) {
      setError('Please enter a valid referrer contact or leave it blank.');
      setLoading(false);
      return;
    }

    if (formData.connectionReason === 'other' && !formData.connectionReasonOther.trim()) {
      setError('Please specify your connection reason.');
      setLoading(false);
      return;
    }

    try {
      // Validate and format phone number if provided
      let formattedPhone: string | null = null;
      if (formData.phoneNumber) {
        const phoneValidation = validateAndFormatUSPhone(formData.phoneNumber);
        if (!phoneValidation.valid) {
          setError(phoneValidation.error || 'Invalid phone number format');
          setLoading(false);
          return;
        }
        formattedPhone = phoneValidation.formatted!;
      }

      // Check if email already exists in waitlist
      const { data: existingWaitlist, error: checkError } = await supabase
        .from('waitlist')
        .select('email')
        .eq('email', formData.email.toLowerCase())
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking waitlist:', checkError);
        setError('Something went wrong. Please try again.');
        setLoading(false);
        return;
      }

      if (existingWaitlist) {
        setError('This email is already on the waitlist!');
        setLoading(false);
        return;
      }

      // Create waitlist entry
      const waitlistData: any = {
        full_name: formData.fullName,
        email: formData.email.toLowerCase(),
        role: formData.role,
        referral_source: formData.referralSource || null,
        phone_number: formattedPhone,
        status: 'pending',
        bonus_credits_awarded: false,
        bonus_credits_amount: 100,
      };

      if (referrerInfo) {
        waitlistData.referred_by = referrerInfo.email;
        waitlistData.referral_code_used = referrerInfo.referral_code;
        waitlistData.connection_reason = formData.connectionReason;
        if (formData.connectionReason === 'other') {
          waitlistData.connection_reason_other = formData.connectionReasonOther;
        }
      }

      const { error: insertError } = await supabase
        .from('waitlist')
        .insert([waitlistData]);

      if (insertError) {
        console.error('Waitlist insert error:', insertError);
        setError('Failed to join waitlist. Please try again.');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Waitlist signup error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            You're on the Waitlist! 🎉
          </h1>
          <p className="text-lg text-gray-700 mb-6">
            Welcome to ResuSwipe! Here's what happens next:
          </p>
          <div className="space-y-4 text-left mb-8">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Get Early Access Updates</h3>
                <p className="text-gray-600">We'll email you exclusive updates and early access opportunities</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Become an Early Tester</h3>
                <p className="text-gray-600">Help shape ResuSwipe by testing new features before anyone else</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Claim Your 100 Bonus Credits</h3>
                <p className="text-gray-600">Unlock premium features instantly when you activate your account</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Redirecting you to home page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left side - Benefits */}
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-8 text-white shadow-2xl">
            <div className="flex items-center gap-2 mb-6">
              <Briefcase className="w-8 h-8" />
              <span className="text-2xl font-bold">ResuSwipe</span>
            </div>

            <h2 className="text-3xl font-bold mb-4">
              Join the Waitlist
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Be among the first to experience the future of job searching
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
                  <Bell className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Exclusive Updates</h3>
                  <p className="text-white/80">
                    Get notified about our launch date, new features, and exclusive early access opportunities
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Early Tester Access</h3>
                  <p className="text-white/80">
                    Help shape the platform by testing features before public release and providing valuable feedback
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Coins className="w-6 h-6 text-amber-900" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">100 Bonus Credits</h3>
                  <p className="text-white/80">
                    Start with 100 free credits when you activate your account - that's $10 worth of premium features!
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-white/10 backdrop-blur rounded-xl border border-white/20">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-amber-300" />
                <span className="font-semibold">Limited Time Offer</span>
              </div>
              <p className="text-sm text-white/80">
                The first 1,000 members on our waitlist will receive premium account status for their first month - absolutely free!
              </p>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Reserve Your Spot
            </h3>
            <p className="text-gray-600 mb-6">
              No password needed - just your basic info to get started
            </p>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition"
                  placeholder="Jane Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition"
                  placeholder="jane@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition"
                  placeholder="(555) 123-4567"
                />
                <p className="text-xs text-gray-500 mt-1">We'll use this to notify you when we launch</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  I'm joining as a *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'job_seeker' })}
                    className={`px-4 py-3 rounded-lg border-2 font-semibold transition ${
                      formData.role === 'job_seeker'
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Users className="w-5 h-5 mx-auto mb-1" />
                    Job Seeker
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, role: 'employer' })}
                    className={`px-4 py-3 rounded-lg border-2 font-semibold transition ${
                      formData.role === 'employer'
                        ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Briefcase className="w-5 h-5 mx-auto mb-1" />
                    Employer
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  How did you hear about us?
                </label>
                <select
                  value={formData.referralSource}
                  onChange={(e) => setFormData({ ...formData, referralSource: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition"
                >
                  <option value="">Select an option</option>
                  <option value="friend">Friend or colleague</option>
                  <option value="social">Social media</option>
                  <option value="search">Search engine</option>
                  <option value="ad">Online advertisement</option>
                  <option value="news">News or article</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Referral section */}
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Referred by someone? (Optional)
                </label>
                <input
                  type="text"
                  value={formData.referrerContact}
                  onChange={(e) => handleReferrerContactChange(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition"
                  placeholder="Enter their email, phone, or referral code"
                />

                {lookingUpReferrer && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Looking up referrer...</span>
                  </div>
                )}

                {referrerInfo && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-green-900">
                        Referred by {referrerInfo.full_name}
                      </p>
                      <p className="text-xs text-green-700">{referrerInfo.email}</p>
                    </div>
                  </div>
                )}

                {referrerError && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">{referrerError}</p>
                  </div>
                )}

                {referrerInfo && (
                  <div className="mt-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      How do you know them? *
                    </label>
                    <select
                      required
                      value={formData.connectionReason}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          connectionReason: e.target.value as ConnectionReason,
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition"
                    >
                      <option value="">Select how you know them</option>
                      <option value="friend">Friend</option>
                      <option value="colleague">Colleague</option>
                      <option value="family">Family</option>
                      <option value="classmate">Classmate</option>
                      <option value="mentor">Mentor/Mentee</option>
                      <option value="other">Other</option>
                    </select>

                    {formData.connectionReason === 'other' && (
                      <input
                        type="text"
                        required
                        value={formData.connectionReasonOther}
                        onChange={(e) =>
                          setFormData({ ...formData, connectionReasonOther: e.target.value })
                        }
                        className="mt-2 w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-500 focus:outline-none transition"
                        placeholder="Please specify..."
                      />
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-bold hover:from-cyan-600 hover:to-blue-700 transition shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Joining Waitlist...
                  </>
                ) : (
                  <>
                    Join Waitlist & Get 100 Credits
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-xs text-center text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-cyan-600 hover:text-cyan-700 font-semibold">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
